import type { DayAvailability, SlotAvailability } from "@/types";

import { scheduleTimeToHHmm } from "./visitAt";

export interface OverCapacityDetails {
  maxCapacity: number;
  bookedCount: number;
  availableSpots: number;
}

export function slotTimeKey(slot: SlotAvailability): string {
  return scheduleTimeToHHmm(slot.start_time);
}

/**
 * Capacity of the slot a handshake would confirm, when confirming would
 * overbook it; null when the day/slot is unknown or closed, the slot is
 * unlimited, or it still has room.
 */
export function overCapacityDetails(
  days: readonly DayAvailability[],
  date: string,
  timeSlot: string,
): OverCapacityDetails | null {
  const day = days.find((entry) => entry.date === date);
  if (!day || day.is_closed) {
    return null;
  }
  const timeKey = scheduleTimeToHHmm(timeSlot);
  const slot = day.slots.find((entry) => slotTimeKey(entry) === timeKey);
  // Limited slots always report booked_count; null only for partner remaining-only slots.
  if (
    !slot ||
    slot.max_capacity == null ||
    slot.booked_count == null ||
    slot.is_available
  ) {
    return null;
  }
  return {
    maxCapacity: slot.max_capacity,
    bookedCount: slot.booked_count,
    availableSpots: slot.available_spots ?? 0,
  };
}

export function formatSlotCapacity(
  slot: SlotAvailability,
  t: (key: string, opts?: Record<string, unknown>) => string,
  partySize: number,
): string {
  if (slot.max_capacity == null) {
    return t("handshakePages:confirmSlotUnlimited");
  }
  const remaining = slot.available_spots ?? 0;
  if (slot.is_available) {
    return t("handshakePages:confirmSlotSpotsRemaining", { count: remaining });
  }
  if (remaining > 0 && remaining < partySize) {
    return t("handshakePages:confirmSlotInsufficient", {
      remaining,
      partySize,
    });
  }
  return t("handshakePages:confirmSlotFull");
}

/**
 * Slot preselected in the confirm modal: the user's pick if the day still
 * offers it, else the requested time on the requested day, else the first slot.
 */
export function resolveSelectedSlotKey({
  canPickScheduledSlot,
  slotKeys,
  selectedSlotKey,
  visitDate,
  requestedVisitDate,
  requestedSlotKey,
}: {
  canPickScheduledSlot: boolean;
  slotKeys: readonly string[];
  selectedSlotKey: string | null;
  visitDate: string;
  /** Activity-local date of the requested visit, null when none was requested. */
  requestedVisitDate: string | null;
  requestedSlotKey: string | null;
}): string | null {
  if (!canPickScheduledSlot) {
    return null;
  }
  if (selectedSlotKey && slotKeys.includes(selectedSlotKey)) {
    return selectedSlotKey;
  }
  if (
    visitDate === requestedVisitDate &&
    requestedSlotKey &&
    slotKeys.includes(requestedSlotKey)
  ) {
    return requestedSlotKey;
  }
  return slotKeys[0] ?? null;
}
